import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import HollowButton from "components/HollowButton";

import { ECouncilVotingType, ICouncilAgendaItem } from "dto/Council/ICouncil";
import {
  ISaveAnalogCouncilVotingRequest,
  IStartCouncilVotingRequest,
} from "dto/Council/ICouncilPortal";

import { decodeText } from "./decodeText";
import styles from "./styles.module.scss";

interface Props {
  /** Punkt, nad którym głosujemy — wskazany wcześniej na liście porządku obrad. */
  item: ICouncilAgendaItem;
  loading?: boolean;
  onClose: () => void;
  onStart: (request: IStartCouncilVotingRequest) => void;
  onSaveAnalog: (request: ISaveAnalogCouncilVotingRequest) => void;
}

// Metryka głosowania — połączone VotingMetricModal i AnalogVotingMetricModal z Magistratu.
// W jednym oknie, bo różnią się tylko tym, skąd biorą się liczby: z portalu (radni
// głosują na żywo) czy z ręki protokolanta (głosowanie przez podniesienie ręki).

const TYPE_OPTIONS: { value: ECouncilVotingType; label: string }[] = [
  { value: ECouncilVotingType.Normal, label: "Większość zwykła" },
  { value: ECouncilVotingType.AbsoluteVer1, label: "Większość bezwzględna" },
  {
    value: ECouncilVotingType.AbsoluteFull,
    label: "Większość bezwzględna ustawowego składu",
  },
  { value: ECouncilVotingType.Qualified, label: "Większość kwalifikowana" },
  { value: ECouncilVotingType.Custom, label: "Wybór jednego z kilku" },
];

const VotingModal = ({ item, loading = false, onClose, onStart, onSaveAnalog }: Props) => {
  const [analog, setAnalog] = useState(false);
  const [type, setType] = useState<ECouncilVotingType>(ECouncilVotingType.Normal);
  const [description, setDescription] = useState(
    // Domyślny opis to treść punktu — w praktyce głosuje się dokładnie nad nim.
    decodeText(item.Title),
  );
  const [classified, setClassified] = useState(false);
  const [numerator, setNumerator] = useState("2");
  const [denominator, setDenominator] = useState("3");
  const [options, setOptions] = useState<string[]>(["", ""]);

  // Projekty aktów dołączone do tego punktu. Gdy jest dokładnie jeden, zakładamy, że
  // głosowanie dotyczy właśnie jego — Magistrat pytał o to prowadzącego osobnym
  // komunikatem („Czy głosowanie dotyczy załączonego w tym punkcie dokumentu?").
  const actAttachments = useMemo(
    () => (item.Attachments ?? []).filter((a) => !!a.LegalActId),
    [item.Attachments],
  );

  const [legalActId, setLegalActId] = useState<string>(
    actAttachments.length === 1 ? actAttachments[0].LegalActId ?? "" : "",
  );
  const [forVotes, setForVotes] = useState("0");
  const [against, setAgainst] = useState("0");
  const [abstain, setAbstain] = useState("0");

  const isCustom = type === ECouncilVotingType.Custom;
  const isQualified = type === ECouncilVotingType.Qualified;

  const filledOptions = useMemo(
    () => options.map((o) => o.trim()).filter(Boolean),
    [options],
  );

  const num = Number(numerator);
  const den = Number(denominator);

  // Wybór jednego z kilku wymaga opcji, a opcji nie da się obsłużyć „z ręki" —
  // BE odrzuca taką kombinację, więc blokujemy ją już tutaj.
  const optionsOk = !isCustom || (!analog && filledOptions.length >= 2);

  const canApply =
    !!description.trim() &&
    optionsOk &&
    (!isQualified || (num > 0 && den > 0 && num <= den));

  const submit = () => {
    if (!canApply) return;

    if (analog) {
      onSaveAnalog({
        AgendaItemId: item.Id,
        Type: type,
        Description: description.trim(),
        For: Number(forVotes) || 0,
        Against: Number(against) || 0,
        Abstain: Number(abstain) || 0,
        QualifiedNumerator: isQualified ? num : null,
        QualifiedDenominator: isQualified ? den : null,
      });

      return;
    }

    onStart({
      AgendaItemId: item.Id,
      Type: type,
      Description: description.trim(),
      IsClassified: classified,
      LegalActId: legalActId || null,
      QualifiedNumerator: isQualified ? num : null,
      QualifiedDenominator: isQualified ? den : null,
      Options: isCustom
        ? filledOptions.map((name, index) => ({ Name: name, Order: index + 1 }))
        : [],
    });
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTitle}>
          Głosowanie — punkt {item.Order}
        </div>

        <div className={styles.modalBody}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Przedmiot głosowania</label>
            <textarea
              className={styles.textArea}
              value={description}
              autoFocus
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {!!actAttachments.length && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Głosowanie dotyczy aktu</label>
              <select
                className={styles.textInput}
                value={legalActId}
                onChange={(e) => setLegalActId(e.target.value)}
              >
                <option value="">— bez powiązania z aktem —</option>
                {actAttachments.map((a) => (
                  <option key={a.Id} value={a.LegalActId ?? ""}>
                    {[a.ActTypeName, a.ActNumber].filter(Boolean).join(" Nr ")}
                    {a.Title ? ` — ${a.Title}` : ""}
                  </option>
                ))}
              </select>
              <span className={styles.fieldLabel}>
                Powiązanie sprawia, że przy akcie w publicznym rejestrze pojawi się wynik
                tego głosowania.
              </span>
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Tryb</label>
            <select
              className={styles.textInput}
              value={type}
              onChange={(e) => setType(Number(e.target.value) as ECouncilVotingType)}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {isQualified && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Próg (ułamek składu rady)</label>
              <div className={styles.fractionRow}>
                <input
                  className={styles.textInput}
                  value={numerator}
                  inputMode="numeric"
                  onChange={(e) => setNumerator(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <span className={styles.fractionSlash}>/</span>
                <input
                  className={styles.textInput}
                  value={denominator}
                  inputMode="numeric"
                  onChange={(e) => setDenominator(e.target.value.replace(/[^0-9]/g, ""))}
                />
              </div>
            </div>
          )}

          <label className={styles.radioOption}>
            <input
              type="checkbox"
              checked={analog}
              onChange={(e) => setAnalog(e.target.checked)}
            />
            Głosowanie przez podniesienie ręki (wynik wpisuję ręcznie)
          </label>

          {analog ? (
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Za</label>
                <input
                  className={styles.textInput}
                  value={forVotes}
                  inputMode="numeric"
                  onChange={(e) => setForVotes(e.target.value.replace(/[^0-9]/g, ""))}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Przeciw</label>
                <input
                  className={styles.textInput}
                  value={against}
                  inputMode="numeric"
                  onChange={(e) => setAgainst(e.target.value.replace(/[^0-9]/g, ""))}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Wstrzymało się</label>
                <input
                  className={styles.textInput}
                  value={abstain}
                  inputMode="numeric"
                  onChange={(e) => setAbstain(e.target.value.replace(/[^0-9]/g, ""))}
                />
              </div>
            </div>
          ) : (
            <>
              <label className={styles.radioOption}>
                <input
                  type="checkbox"
                  checked={classified}
                  onChange={(e) => setClassified(e.target.checked)}
                />
                Głosowanie tajne (bez imiennej listy głosów)
              </label>

              {isCustom && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Opcje do wyboru</label>

                  {options.map((option, index) => (
                    // Opcje nie mają jeszcze identyfikatorów — powstają dopiero na serwerze,
                    // więc kluczem może być tylko pozycja na liście.
                    <div key={`option-${index}`} className={styles.optionRow}>
                      <input
                        className={styles.textInput}
                        value={option}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((v, i) => (i === index ? e.target.value : v)),
                          )
                        }
                      />
                      <button
                        type="button"
                        className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                        title="Usuń opcję"
                        disabled={options.length <= 2}
                        onClick={() =>
                          setOptions((prev) => prev.filter((_, i) => i !== index))
                        }
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    className={styles.addOptionButton}
                    onClick={() => setOptions((prev) => [...prev, ""])}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Dodaj opcję
                  </button>

                  {filledOptions.length < 2 && (
                    <span className={styles.fieldError}>
                      Podaj co najmniej dwie opcje.
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {analog && isCustom && (
            <span className={styles.fieldError}>
              Głosowania z wyborem opcji nie da się wpisać ręcznie — zmień tryb albo
              odznacz podniesienie ręki.
            </span>
          )}
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text={analog ? "Zapisz wynik" : "Rozpocznij głosowanie"}
            loading={loading}
            disabled={!canApply}
            onClick={submit}
          />
        </div>
      </div>
    </div>
  );
};

export default VotingModal;
